// Vollea AuthContext — wraps the api helpers (email/JWT + Emergent Google session)
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, login as apiLogin, register as apiRegister, googleSession as apiGoogleSession, logoutApi, setToken } from "../lib/api";

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const u = await api.get("/auth/me");
      setUser(u);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: When returning from Emergent OAuth, the URL fragment contains
    // session_id=... — the AuthCallback view will exchange it for a token via
    // /api/auth/session and then trigger refresh(). Skip the initial /auth/me
    // check here so we don't race the callback and clear the (not-yet-stored) token.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const data = await apiLogin(email, password);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const data = await apiRegister(payload);
    setUser(data.user);
    return data.user;
  };

  const loginGoogle = async (sessionId, inviteCode) => {
    const data = await apiGoogleSession(sessionId, inviteCode);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    await logoutApi();
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, loginGoogle, logout, refresh, setToken }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);
